import { useMemo } from 'react';
import { RefundSummary } from '../../../../types/sales.types';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';

export interface UseReturnConfirmationModalProps {
  refundSummary: RefundSummary | null;
}

export const useReturnConfirmationModal = ({ refundSummary }: UseReturnConfirmationModalProps) => {
  // Format currency
  const formatCurrency = (amount: number) => {
    return formatCurrencyValue(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formatted values
  const formattedReturnDate = useMemo(
    () => (refundSummary ? formatDate(refundSummary.returnDate) : ''),
    [refundSummary],
  );

  const formattedOriginalTotal = useMemo(
    () => (refundSummary ? formatCurrency(refundSummary.originalTotal) : ''),
    [refundSummary],
  );

  const formattedRestockingFee = useMemo(
    () => (refundSummary ? formatCurrency(refundSummary.restockingFee) : ''),
    [refundSummary],
  );

  const formattedRefundAmount = useMemo(
    () => (refundSummary ? formatCurrency(refundSummary.refundAmount) : ''),
    [refundSummary],
  );

  // Calculate restocking fee percentage
  const restockingFeePercentage = useMemo(() => {
    if (!refundSummary || refundSummary.originalTotal === 0) return 0;
    return ((refundSummary.restockingFee / refundSummary.originalTotal) * 100).toFixed(1);
  }, [refundSummary]);

  // Determine if it's a high adjustment (>25%)
  const isHighAdjustment = useMemo(() => {
    if (!refundSummary) return false;
    return refundSummary.restockingFee > refundSummary.originalTotal * 0.25;
  }, [refundSummary]);

  // Restocking fee label
  const restockingFeeLabel = useMemo(() => {
    if (isHighAdjustment) return 'Ajuste aplicado';
    return `Tarifa de reabastecimiento (${restockingFeePercentage}%)`;
  }, [isHighAdjustment, restockingFeePercentage]);

  // Display user name
  const displayUserName = useMemo(() => {
    if (!refundSummary) return 'Usuario autenticado';
    return refundSummary.processedByName ?? refundSummary.processedBy ?? 'Usuario autenticado';
  }, [refundSummary]);

  // Check if has restocking fee
  const hasRestockingFee = useMemo(
    () => refundSummary && refundSummary.restockingFee > 0,
    [refundSummary],
  );

  const formattedBreakdown = useMemo(() => {
    if (!refundSummary?.itemsBreakdown?.length) return [];
    return refundSummary.itemsBreakdown.map((item) => ({
      ...item,
      originalPriceFormatted: formatCurrencyValue(item.originalPrice),
      restockingFeeFormatted: formatCurrencyValue(item.restockingFee),
      refundAmountFormatted: formatCurrencyValue(item.refundAmount),
    }));
  }, [refundSummary]);

  return {
    formattedReturnDate,
    formattedOriginalTotal,
    formattedRestockingFee,
    formattedRefundAmount,
    restockingFeeLabel,
    displayUserName,
    hasRestockingFee,
    formattedBreakdown,
  };
};
